import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import { RefreshCw, Download, AlertTriangle, TrendingUp } from 'lucide-react';
import { puntovService } from '../../../services/api';
import TablaDatos from './TablaDatos';
import { fmtUsd, fmtFecha, fmtPeriodo, fmtPct, fmtEntero, fmtDiaCorto, diasCompletos, horasCompletas, fmtHora, fmtRangoHora } from './formato';
import { PERIODOS, rangoDe, mesDelRango } from './periodos';
import {
  useAlertasCerradas, useUmbralAlerta, SelectorUmbral, BloqueAlertas, AvisosFueraDeServicio,
  ListaMaquinas, ProductosDelCliente, maquinasFueraDeServicio, nombreProducto
} from './ventasComun';

// Colores validados con el script de la guía de visualización (pasa CVD,
// visión normal y contraste 3:1 sobre blanco). Venta y ganancia comparten
// eje porque ambas son USD.
const COLOR_VENTA = '#2a78d6';
const COLOR_GANANCIA = '#eb6834';

const fmtEje = (v) => `US$ ${Number(v).toLocaleString('es-VE', { maximumFractionDigits: 0 })}`;

const Tarjeta = ({ titulo, valor, detalle }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 min-w-0">
    <div className="text-sm text-gray-500">{titulo}</div>
    <div className="mt-1 text-lg sm:text-2xl font-bold text-gray-900 tabular-nums break-words">{valor}</div>
    {detalle && <div className="mt-0.5 text-xs text-gray-500 tabular-nums">{detalle}</div>}
  </div>
);

const ESTADO_COBRO = {
  por_enviar: { texto: 'Por enviar', clase: 'bg-gray-100 text-gray-700' },
  pendiente: { texto: 'Pendiente de pago', clase: 'bg-amber-100 text-amber-800' },
  en_mora: { texto: 'En mora', clase: 'bg-red-100 text-red-700' },
  pagado: { texto: 'Pagado', clase: 'bg-green-100 text-green-700' }
};

// Lo que Punto V cobra por el mes: el cobro emitido o, si aún no se emitió,
// el estimado con la misma calculadora del día de cobro.
const TarjetaMensualidad = ({ periodo, m, cargando }) => {
  const [mes, anio] = periodo ? fmtPeriodo(periodo).split(' ') : [];
  const titulo = periodo ? `Mensualidad · ${mes.slice(0, 3)} ${anio}` : 'Mensualidad';
  let valor = '—';
  let etiqueta = null;
  let detalle = null;

  if (!periodo) {
    detalle = 'Elija un solo mes para ver la mensualidad';
  } else if (cargando || !m) {
    detalle = cargando ? 'Consultando…' : null;
  } else if (m.estado === 'emitido') {
    valor = fmtUsd(m.monto_usd);
    etiqueta = ESTADO_COBRO[m.cobro.estado] || { texto: m.cobro.estado, clase: 'bg-gray-100 text-gray-700' };
    detalle = [m.explicacion, m.cobro.cotizacion_numero].filter(Boolean).join(' · ');
  } else if (m.estado === 'estimado') {
    valor = fmtUsd(m.monto_usd);
    etiqueta = { texto: 'Estimado', clase: 'bg-blue-50 text-blue-700' };
    detalle = m.explicacion || 'Sin máquinas cobrables en el mes';
  } else if (m.estado === 'cortesia') {
    valor = 'Cortesía';
    detalle = m.motivo ? `No se cobra: ${m.motivo}` : 'Este mes no se cobra';
  } else if (m.estado === 'cobro_desactivado') {
    valor = 'Sin cobro';
    detalle = 'El cobro de este cliente está desactivado';
  } else {
    valor = 'Sin cobro';
    detalle = 'Este cliente no tiene cobro configurado';
  }

  return (
    <div className="bg-white rounded-xl border border-blue-200 p-3 sm:p-4 min-w-0">
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm text-gray-500">{titulo}</div>
        {etiqueta && <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium whitespace-nowrap ${etiqueta.clase}`}>{etiqueta.texto}</span>}
      </div>
      <div className="mt-1 text-lg sm:text-2xl font-bold text-gray-900 tabular-nums break-words">{valor}</div>
      {detalle && <div className="mt-0.5 text-xs text-gray-500">{detalle}</div>}
    </div>
  );
};

const TooltipDia = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs space-y-0.5">
      <div className="font-semibold text-gray-800">{d.dia ? fmtFecha(d.dia) : fmtRangoHora(d.hora)}</div>
      <div className="flex items-center gap-1.5 text-gray-700"><span className="w-2.5 h-0.5 rounded" style={{ background: COLOR_VENTA }} /> Venta <span className="ml-auto pl-3 font-medium tabular-nums">{fmtUsd(d.venta)}</span></div>
      <div className="flex items-center gap-1.5 text-gray-700"><span className="w-2.5 h-0.5 rounded" style={{ background: COLOR_GANANCIA }} /> Ganancia <span className="ml-auto pl-3 font-medium tabular-nums">{fmtUsd(d.ganancia)}</span></div>
      <div className="text-gray-500 tabular-nums">{fmtEntero(d.n_ventas)} ventas · margen {fmtPct(d.margen_pct)}</div>
    </div>
  );
};

const TooltipMaquina = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const m = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs space-y-0.5">
      <div className="font-semibold text-gray-800">{m.id_maquina}{m.descripcion ? ` · ${m.descripcion}` : ''}</div>
      <div className="text-gray-700 tabular-nums">Venta {fmtUsd(m.venta)} · Ganancia {fmtUsd(m.ganancia)}</div>
      <div className="text-gray-500 tabular-nums">{fmtEntero(m.n_ventas)} ventas · margen {fmtPct(m.margen_pct)}</div>
    </div>
  );
};

const columnasMaquinas = [
  {
    id: 'maquina', titulo: 'Máquina', ordenable: true, valor: (m) => m.id_maquina,
    render: (m) => (
      <>
        <div className="font-mono text-xs text-gray-800">{m.id_maquina}</div>
        <div className="text-xs text-gray-400">{m.descripcion || '—'}{m.activa ? '' : ' · inactiva'}</div>
      </>
    )
  },
  { id: 'n_ventas', titulo: 'Ventas', ordenable: true, alinear: 'right', valor: (m) => m.n_ventas, render: (m) => <span className="tabular-nums">{fmtEntero(m.n_ventas)}</span> },
  { id: 'unidades', titulo: 'Unidades', ordenable: true, alinear: 'right', valor: (m) => m.unidades, render: (m) => <span className="tabular-nums">{fmtEntero(m.unidades)}</span> },
  { id: 'venta', titulo: 'Venta', ordenable: true, alinear: 'right', valor: (m) => m.venta, render: (m) => <span className="tabular-nums font-medium">{fmtUsd(m.venta)}</span> },
  { id: 'costo', titulo: 'Costo', ordenable: true, alinear: 'right', valor: (m) => m.costo, render: (m) => <span className="tabular-nums text-gray-600">{fmtUsd(m.costo)}</span> },
  { id: 'ganancia', titulo: 'Ganancia', ordenable: true, alinear: 'right', valor: (m) => m.ganancia, render: (m) => <span className="tabular-nums">{fmtUsd(m.ganancia)}</span> },
  { id: 'margen', titulo: 'Margen', ordenable: true, alinear: 'right', valor: (m) => m.margen_pct ?? -1, render: (m) => <span className="tabular-nums">{fmtPct(m.margen_pct)}</span> },
  {
    id: 'mas_vendido', titulo: 'Más vendido', ordenable: true, valor: (m) => (m.productos?.[0] ? nombreProducto(m.productos[0]) : ''),
    render: (m) => (m.productos?.[0]
      ? <span className="text-xs">{nombreProducto(m.productos[0])} <span className="text-gray-400 tabular-nums">· {fmtEntero(m.productos[0].unidades)} u</span></span>
      : <span className="text-xs text-gray-400">—</span>)
  }
];
const claveMaquina = (m) => m.id_maquina;
const textoMaquina = (m) => `${m.id_maquina} ${m.descripcion || ''}`;

const VentasPuntoV = () => {
  const [clientes, setClientes] = useState([]);
  const [empresa, setEmpresa] = useState('');
  const [periodo, setPeriodo] = useState('hoy');
  const [rango, setRango] = useState(() => rangoDe('hoy'));
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [error, setError] = useState('');
  const [mensualidad, setMensualidad] = useState(null);
  const [cargandoMensualidad, setCargandoMensualidad] = useState(false);
  const [ultimaActualizacion, setUltimaActualizacion] = useState(null);
  // Mismas alertas cerradas y mismo tiempo de alerta que el Monitor.
  const { ocultas, ocultar: ocultarAlertas, mostrar: mostrarAlertas } = useAlertasCerradas();
  const [umbral, elegirUmbral] = useUmbralAlerta();

  useEffect(() => {
    puntovService.listarClientes()
      .then(res => {
        const conBase = (res.data || []).filter(c => c.base_existe !== false && !c.archivado);
        setClientes(conBase);
        let guardada = null;
        try { guardada = localStorage.getItem('puntov.ventas.empresa'); } catch { guardada = null; }
        const inicial = conBase.find(c => c.base_datos === guardada) || conBase[0];
        if (inicial) setEmpresa(inicial.base_datos);
      })
      .catch(err => setError(err.message || 'No se pudieron cargar los clientes'));
  }, []);

  const [desde, hasta] = periodo === 'rango' ? rango : rangoDe(periodo);

  // Sin actualización automática: se consulta al cambiar cliente, periodo o
  // tiempo de alerta, y con el botón Actualizar.
  const peticion = useRef(0);
  const cargar = useCallback(async () => {
    if (!empresa || !desde || !hasta || desde > hasta) return;
    const mia = ++peticion.current;
    setLoading(true);
    setError('');
    try {
      const res = await puntovService.ventas(empresa, desde, hasta, { alertaMin: umbral });
      if (mia !== peticion.current) return;
      setDatos(res.data);
      setUltimaActualizacion(new Date());
    } catch (err) {
      if (mia !== peticion.current) return;
      setDatos(null);
      setError(err.message || 'No se pudieron consultar las ventas');
    } finally {
      if (mia === peticion.current) setLoading(false);
    }
  }, [empresa, desde, hasta, umbral]);

  useEffect(() => {
    setDatos(null);
    cargar();
    return () => { peticion.current++; };
  }, [cargar]);

  const periodoMes = mesDelRango(desde, hasta);

  useEffect(() => {
    if (!empresa || !periodoMes) { setMensualidad(null); return undefined; }
    let vigente = true;
    setCargandoMensualidad(true);
    puntovService.mensualidad(empresa, periodoMes)
      .then(res => { if (vigente) setMensualidad(res.data); })
      .catch(() => { if (vigente) setMensualidad(null); })
      .finally(() => { if (vigente) setCargandoMensualidad(false); });
    return () => { vigente = false; };
  }, [empresa, periodoMes]);

  const elegirEmpresa = (valor) => {
    setEmpresa(valor);
    try { localStorage.setItem('puntov.ventas.empresa', valor); } catch { /* sin almacenamiento local */ }
  };

  const elegirPeriodo = (valor) => {
    if (valor === 'rango') setRango([desde, hasta]);
    setPeriodo(valor);
  };

  const exportar = async () => {
    setExportando(true);
    try {
      const blob = await puntovService.ventasExcel(empresa, desde, hasta);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ventas-${empresa.replace(/^esgamanc_/, '')}-${desde}_${hasta}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      setError(err.message);
    } finally {
      setExportando(false);
    }
  };

  const serieDiaria = useMemo(
    () => (datos ? diasCompletos(datos.por_dia, datos.fecha_desde, datos.fecha_hasta) : []),
    [datos]
  );
  const maquinasConVenta = useMemo(() => (datos ? datos.por_maquina.filter(m => m.venta > 0) : []), [datos]);
  // El eje muestra la descripción de la máquina; el id queda en el tooltip.
  const nombreEnEje = useMemo(() => {
    const nombres = new Map(maquinasConVenta.map(m => [m.id_maquina, m.descripcion?.trim() || m.id_maquina]));
    return (id) => {
      const nombre = nombres.get(id) || id;
      return nombre.length > 24 ? `${nombre.slice(0, 23)}…` : nombre;
    };
  }, [maquinasConVenta]);
  const t = datos?.totales;
  const sinVentas = t && t.n_ventas === 0;
  const claseControl = 'h-9 px-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="block text-xs font-medium text-gray-600 mb-1">Cliente</span>
          <select value={empresa} onChange={(e) => elegirEmpresa(e.target.value)} className={`${claseControl} min-w-[11rem]`}>
            {clientes.length === 0 && <option value="">Sin clientes con base</option>}
            {clientes.map(c => <option key={c.base_datos} value={c.base_datos}>{c.slug}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-600 mb-1">Periodo</span>
          <select value={periodo} onChange={(e) => elegirPeriodo(e.target.value)} className={claseControl}>
            {PERIODOS.map(p => <option key={p.id} value={p.id}>{p.etiqueta}</option>)}
          </select>
        </label>
        {periodo === 'rango' && (
          <>
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Desde</span>
              <input type="date" value={desde} max={hasta} onChange={(e) => setRango([e.target.value, hasta])} className={claseControl} />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Hasta</span>
              <input type="date" value={hasta} min={desde} onChange={(e) => setRango([desde, e.target.value])} className={claseControl} />
            </label>
          </>
        )}
        <SelectorUmbral umbral={umbral} onCambiar={elegirUmbral} className={claseControl} />
        <div className="flex items-center gap-2 ml-auto">
          {ultimaActualizacion && (
            <span className="text-xs text-gray-500 tabular-nums">Actualizado a las {ultimaActualizacion.toLocaleTimeString('es-VE')}</span>
          )}
          <AvisosFueraDeServicio
            grupos={datos ? [{ base: datos.empresa, nombre: datos.empresa.replace(/^esgamanc_/, ''), maquinas: maquinasFueraDeServicio(datos) }] : []}
            dias={datos?.dias_fuera_de_servicio}
          />
          <button onClick={cargar} disabled={loading || !empresa} className="h-9 px-3 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm disabled:opacity-60">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Actualizar
          </button>
          <button onClick={exportar} disabled={exportando || !datos || sinVentas} className="h-9 px-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm disabled:opacity-50">
            <Download size={15} /> {exportando ? 'Generando…' : 'Excel'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> <span>{error}</span>
        </div>
      )}

      {loading && !datos && (
        <div className="text-center py-16">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      )}

      {datos && (
        <div className={`space-y-4 transition-opacity ${loading ? 'opacity-60' : ''}`}>
          <p className="text-xs text-gray-500">
            {datos.empresa.replace(/^esgamanc_/, '')} · {fmtFecha(datos.fecha_desde)} a {fmtFecha(datos.fecha_hasta)} · montos en USD
          </p>

          {datos.maquinas_sin_tablas?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-3 text-sm flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">
                  {datos.maquinas_sin_tablas.length === 1 ? '1 máquina no se incluye' : `${datos.maquinas_sin_tablas.length} máquinas no se incluyen`}
                  {' '}en estos totales: sus tablas de ventas no existen con el nombre registrado en Punto V.
                </p>
                <ul className="mt-1 space-y-0.5 text-xs">
                  {datos.maquinas_sin_tablas.map(m => (
                    <li key={m.id_maquina}>
                      <span className="font-mono">{m.id_maquina}</span>: falta {m.tablas_faltantes.join(' y ')}
                      {m.tablas_parecidas.length > 0 && <> (en la base existe {m.tablas_parecidas.join(' y ')}, con otras mayúsculas)</>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            <Tarjeta titulo="Venta" valor={fmtUsd(t.venta)} detalle={`${fmtEntero(t.n_ventas)} ventas · ${fmtEntero(t.unidades)} unidades`} />
            <Tarjeta titulo="Ganancia" valor={fmtUsd(t.ganancia)} detalle={`Costo ${fmtUsd(t.costo)}`} />
            <Tarjeta titulo="Margen bruto" valor={fmtPct(t.margen_pct)} detalle="(venta − costo) / venta" />
            <Tarjeta titulo="Ticket promedio" valor={t.ticket_promedio === null ? '—' : fmtUsd(t.ticket_promedio)} detalle="Venta por transacción" />
            <TarjetaMensualidad periodo={periodoMes} m={mensualidad} cargando={cargandoMensualidad} />
          </div>

          <BloqueAlertas base={datos.empresa} datos={datos} ocultas={ocultas} onOcultar={ocultarAlertas} onMostrar={mostrarAlertas} />

          {sinVentas ? (
            <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center text-gray-500">
              <TrendingUp size={28} className="mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No hay ventas aprobadas en este periodo.</p>
              <p className="text-xs text-gray-400 mt-1">Pruebe otro periodo o verifique que las máquinas estén activas.</p>
            </div>
          ) : (
            <>
              {/* Con un solo día la serie diaria sería un punto: se muestra por hora. */}
              {datos.fecha_desde === datos.fecha_hasta ? (
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">Venta y ganancia por hora</h3>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={horasCompletas(datos.por_hora)} margin={{ top: 8, right: 16, bottom: 0, left: 8 }} barGap={1} barCategoryGap="20%">
                        <CartesianGrid stroke="#eef0f3" vertical={false} />
                        <XAxis dataKey="hora" tickFormatter={fmtHora} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} interval={1} />
                        <YAxis tickFormatter={fmtEje} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} width={72} />
                        <Tooltip content={<TooltipDia />} cursor={{ fill: '#f3f4f6' }} />
                        <Legend verticalAlign="top" align="right" height={28} itemSorter={null} iconType="square" wrapperStyle={{ fontSize: 12, color: '#374151' }} />
                        <Bar dataKey="venta" name="Venta" fill={COLOR_VENTA} radius={[3, 3, 0, 0]} />
                        <Bar dataKey="ganancia" name="Ganancia" fill={COLOR_GANANCIA} radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Venta y ganancia por día</h3>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={serieDiaria} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
                      <CartesianGrid stroke="#eef0f3" vertical={false} />
                      <XAxis dataKey="dia" tickFormatter={fmtDiaCorto} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} minTickGap={24} />
                      <YAxis tickFormatter={fmtEje} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} width={72} />
                      <Tooltip content={<TooltipDia />} cursor={{ stroke: '#9ca3af', strokeDasharray: '3 3' }} />
                      <Legend verticalAlign="top" align="right" height={28} itemSorter={null} iconType="plainline" wrapperStyle={{ fontSize: 12, color: '#374151' }} />
                      <Line type="linear" dataKey="venta" name="Venta" stroke={COLOR_VENTA} strokeWidth={2} dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }} />
                      <Line type="linear" dataKey="ganancia" name="Ganancia" stroke={COLOR_GANANCIA} strokeWidth={2} dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              )}

              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Venta y ganancia por máquina</h3>
                <div style={{ height: Math.max(140, maquinasConVenta.length * 52 + 40) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={maquinasConVenta} layout="vertical" margin={{ top: 0, right: 16, bottom: 0, left: 8 }} barGap={2} barCategoryGap="28%">
                      <CartesianGrid stroke="#eef0f3" horizontal={false} />
                      <XAxis type="number" tickFormatter={fmtEje} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} />
                      <YAxis type="category" dataKey="id_maquina" tickFormatter={nombreEnEje} tick={{ fontSize: 11, fill: '#374151' }} tickLine={false} axisLine={false} width={160} />
                      <Tooltip content={<TooltipMaquina />} cursor={{ fill: '#f3f4f6' }} />
                      <Legend verticalAlign="top" align="right" height={28} itemSorter={null} iconType="square" wrapperStyle={{ fontSize: 12, color: '#374151' }} />
                      <Bar dataKey="venta" name="Venta" fill={COLOR_VENTA} radius={[0, 4, 4, 0]} />
                      <Bar dataKey="ganancia" name="Ganancia" fill={COLOR_GANANCIA} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}

          {/* Las mismas tarjetas de máquina y top de productos del Monitor. */}
          {datos.por_maquina.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <ListaMaquinas maquinas={datos.por_maquina} />
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                {datos.por_producto?.length > 0
                  ? <ProductosDelCliente productos={datos.por_producto} />
                  : <p className="text-sm text-gray-500">Sin productos vendidos en este periodo.</p>}
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-800">Detalle por máquina</h3>
            </div>
            <TablaDatos
              columnas={columnasMaquinas}
              filas={datos.por_maquina}
              clave={claveMaquina}
              textoBusqueda={textoMaquina}
              placeholderBusqueda="Buscar máquina"
              vacio="Este cliente no tiene máquinas"
            />
          </div>

          <p className="text-xs text-gray-400">
            Fuente: Punto V, solo ventas aprobadas, con el precio efectivo de cada posición y el costo registrado en cada venta
            (la misma regla del reporte "Detalle general" de Punto V Manager).
          </p>
        </div>
      )}
    </div>
  );
};

export default VentasPuntoV;
